import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import type {
  ICustomProperties,
  ITelemetryItem,
} from "@microsoft/applicationinsights-web";
import { appEnv } from "@/constants/env";
import type {
  ClientErrorSource,
  TelemetryEventName,
  TelemetryEvents,
} from "./events";
import { eventTiers } from "./events";

declare global {
  interface Window {
    hasPerformanceConsent?: boolean;
  }
}

export interface TelemetryContext {
  language: string;
  province?: string;
  flow: string;
  route?: string;
}

export interface DependencyCall {
  method: string;
  endpoint: string;
  status?: number;
  durationMs: number;
}

type TrackEventArgs<TName extends TelemetryEventName> =
  keyof TelemetryEvents[TName] extends never
    ? []
    : [properties: TelemetryEvents[TName]];

const consentChangeEvent = "consentchange";
const pageViewPerformanceType = "PageviewPerformanceData";

let appInsights: ApplicationInsights | undefined;
let getContext: (() => TelemetryContext) | undefined;
let hasAnalyticsConsent = false;
let isTrackingBrowserTimings = false;

const safely = (action: () => void) => {
  try {
    action();
  } catch {
    return;
  }
};

const omitNullish = (properties: object = {}): ICustomProperties =>
  Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value != null),
  );

const getHost = (url: string) => {
  try {
    return new URL(url).host || undefined;
  } catch {
    return undefined;
  }
};

const serialize = (value: unknown) => {
  if (typeof value === "string") return value;

  const seen = new WeakSet<object>();

  try {
    return (
      JSON.stringify(value, (_key, item: unknown) => {
        if (typeof item !== "object" || item === null) return item;
        if (seen.has(item)) return "[Circular]";
        seen.add(item);
        return item;
      }) ?? String(value)
    );
  } catch {
    return String(value);
  }
};

const toError = (reason: unknown) => {
  if (reason instanceof Error) return reason;

  const { name, message, stack } = Object(reason);
  const error = new Error(
    typeof message === "string" ? message : serialize(reason),
  );

  if (typeof name === "string") error.name = name;
  if (typeof stack === "string") error.stack = stack;

  return error;
};

const newDependencyId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

const startTimer = () => {
  const startedAt = performance.now();

  return () => Math.round(performance.now() - startedAt);
};

const getCommonProperties = () =>
  omitNullish({
    environment: appEnv.environment,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    ...getContext?.(),
  });

const logToConsole = (name: string, properties?: ICustomProperties) => {
  // eslint-disable-next-line no-console
  console.debug(`[telemetry] ${name}`, {
    ...getCommonProperties(),
    ...properties,
  });
};

const trackEvent = <TName extends TelemetryEventName>(
  name: TName,
  ...[properties]: TrackEventArgs<TName>
) =>
  safely(() => {
    if (eventTiers[name] === "analytics" && !hasAnalyticsConsent) return;

    const customProperties = omitNullish(properties);

    if (appEnv.isDevelopment) logToConsole(name, customProperties);
    else appInsights?.trackEvent({ name }, customProperties);
  });

const trackException = (exception: Error, properties?: ICustomProperties) =>
  safely(() => {
    const customProperties = omitNullish(properties);

    if (appEnv.isDevelopment)
      logToConsole(`exception ${exception.message}`, customProperties);
    else appInsights?.trackException({ exception }, customProperties);
  });

const trackClientError = (
  error: unknown,
  source: ClientErrorSource,
  componentStack?: string,
) => {
  const exception = toError(error);

  trackException(exception, { source, component_stack: componentStack });
  trackEvent("client_error", {
    message: exception.message,
    source,
    component_stack: componentStack,
  });
};

const trackDependency = ({
  method,
  endpoint,
  status = 0,
  durationMs,
}: DependencyCall) =>
  safely(() => {
    const name = `${method} ${endpoint}`;

    if (appEnv.isDevelopment)
      logToConsole(`dependency ${name}`, { status, duration_ms: durationMs });
    else
      appInsights?.trackDependencyData({
        id: newDependencyId(),
        name,
        target: `${appEnv.apiUrl}${endpoint}`,
        type: "Ajax",
        duration: durationMs,
        success: status >= 200 && status < 400,
        responseCode: status,
      });
  });

const trackPageView = () =>
  safely(() => {
    const route = getContext?.().route;

    if (!route || !hasAnalyticsConsent) return;

    if (appEnv.isDevelopment) logToConsole(`page_view ${route}`);
    else
      appInsights?.trackPageView({
        name: route,
        uri: `${window.location.origin}${route}`,
        refUri: getHost(document.referrer) ?? "",
      });
  });

const trackBrowserTimings = () =>
  safely(() => {
    const route = getContext?.().route;

    try {
      isTrackingBrowserTimings = true;
      appInsights?.trackPageViewPerformance({
        name: route,
        uri: route && `${window.location.origin}${route}`,
      });
    } finally {
      isTrackingBrowserTimings = false;
    }
  });

const applyTelemetryRules = (item: ITelemetryItem) => {
  if (item.baseType === pageViewPerformanceType && !isTrackingBrowserTimings)
    return false;

  delete item.ext?.user;
  item.data = { ...getCommonProperties(), ...item.data };
};

const handleConsentChange = () => {
  const hadConsent = hasAnalyticsConsent;

  hasAnalyticsConsent = !!window.hasPerformanceConsent;

  if (hasAnalyticsConsent && !hadConsent) trackPageView();
};

const flush = () => safely(() => appInsights?.onunloadFlush());

const initialize = (options: { getContext: () => TelemetryContext }) => {
  if (getContext) return;

  getContext = options.getContext;
  hasAnalyticsConsent = !!window.hasPerformanceConsent;

  window.addEventListener(consentChangeEvent, handleConsentChange);
  window.addEventListener("error", (event) =>
    trackClientError(event.error ?? event.message, "window_error"),
  );
  window.addEventListener("unhandledrejection", (event) =>
    trackClientError(event.reason, "unhandled_rejection"),
  );

  if (appEnv.isDevelopment || !appEnv.appInsightsConnectionString) return;

  safely(() => {
    const instance = new ApplicationInsights({
      config: {
        connectionString: appEnv.appInsightsConnectionString,
        disableExceptionTracking: true,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        disableCookiesUsage: true,
        isStorageUseDisabled: true,
      },
    });
    instance.loadAppInsights();
    instance.context.application.ver = appEnv.appVersion;
    instance.addTelemetryInitializer(applyTelemetryRules);
    appInsights = instance;
  });

  if (document.readyState === "complete") setTimeout(trackBrowserTimings);
  else window.addEventListener("load", () => setTimeout(trackBrowserTimings));
};

export const telemetry = {
  initialize,
  trackEvent,
  trackException,
  trackClientError,
  trackDependency,
  trackPageView,
  flush,
  startTimer,
  getHost,
};
